// Host-side row-pivot helpers shared by sunburst, sankey, tree, and
// any future hierarchical / graph chart that takes flat rows from
// author SQL and reshapes them for ECharts.
//
// Security: author-controlled `name` / `parent` / `id` / `source` /
// `target` strings flow into object keys here. Round-4 security M-2 /
// M-5 require prototype-safe lookup tables — never plain `{}`
// literals. `Map` and `Object.create(null)` are the only two options
// that don't inherit `__proto__` etc.

/**
 * Hierarchy tree node shape consumed by ECharts' SunburstSeries and
 * TreeSeries. Each node has a name, an optional value, and optional
 * children. The display name is what ECharts renders; the `id`
 * (carried only when the author opted in via `idKey`) is the stable
 * key used for parent lookup so display names can repeat across
 * branches.
 *
 * `colorKey` carries one extra column's value through to the chart
 * component so that `colorBy: "<column>"` modes (currently `tree`
 * only) can pick a palette entry per node without re-reading the
 * source rows. Undefined when the caller didn't pass a `colorByKey`.
 */
export interface HierarchyTreeNode {
  name: string;
  value?: number;
  children?: HierarchyTreeNode[];
  /**
   * Stable identifier used for parent lookup during pivot. Defaults
   * to `name` when the caller didn't pass an `idKey`. ECharts ignores
   * this field; it's preserved on the node so callers (e.g. cycle /
   * orphan diagnostics) can reference the row identity that
   * collided.
   */
  id?: string;
  /** Carried column value for downstream `colorBy: "<column>"` modes. */
  colorKey?: unknown;
}

/** Back-compat alias for the previous `SunburstTreeNode` export. */
export type SunburstTreeNode = HierarchyTreeNode;

/**
 * Pivot helper options. The `idKey` and `colorByKey` fields are
 * additive: callers that don't set them get the historical
 * "name-is-identity, no colour column" behaviour.
 */
export interface PivotHierarchyOptions {
  /** Column holding the parent reference. Empty / missing → root. */
  parentKey: string;
  /** Column holding the node's display name. */
  nameKey: string;
  /**
   * Column holding the node's numeric value. Optional — when omitted,
   * every node's `value` ends up `undefined` (the calling chart
   * decides whether that's allowed; sunburst rejects, tree allows).
   */
  valueKey?: string;
  /**
   * Column holding a stable identifier per row. When set, identity is
   * keyed by this column so display names can repeat across the
   * tree (e.g. "Beginners" under both "Squash" and "Tennis"). When
   * omitted, identity falls back to `nameKey` — the legacy behaviour
   * that requires tree-wide unique names.
   *
   * Parent references resolve against the chosen identity column:
   *   - `idKey` set    → `parent` column holds the parent's id
   *   - `idKey` unset  → `parent` column holds the parent's name
   *
   * Authors with repeating names *must* set `idKey`. The pivot can't
   * disambiguate from `(parent, name)` alone past depth 2.
   */
  idKey?: string;
  /**
   * Column whose value should be carried onto each node as
   * `colorKey`. Used by `colorBy: "<column>"` rendering modes. When
   * omitted, no colour metadata travels with the node.
   */
  colorByKey?: string;
}

/**
 * Pivot flat `{ parent, name, value }` rows into ECharts' nested tree
 * shape. Returns the root array (the tree's top level) plus
 * diagnostics for cycles and orphan parent references.
 *
 * Rules:
 *  - Rows with `parent === ""` (or missing) become root-level nodes.
 *  - Rows whose `parent` references an identifier not present in the
 *    input are attached as roots and counted in `orphanCount` so the
 *    caller can emit a diagnostic.
 *  - Cycles (a node's parent chain returns to itself) are detected;
 *    cycle nodes are returned in `cyclicNames` (display names, for
 *    operator legibility) so the caller can warn via lucent-
 *    diagnostic without throwing. Cycle nodes are pinned at root.
 *  - Identity column: when `idKey` is set, each row's id is the value
 *    of that column (string-coerced); when unset, id == name. Two
 *    rows sharing an id silently overwrite each other — that's the
 *    contract authors are expected to honour, and the diagnostic
 *    surface to recover from is `tree_multiple_roots` / per-chart
 *    rejection rather than something this helper enforces.
 *  - Depth cap: caller enforces. `hierarchyMaxDepth(roots)` exposes
 *    a max-depth measurement so the chart component can fail soft.
 */
export function pivotHierarchyRows(
  rows: ReadonlyArray<Record<string, unknown>>,
  opts: PivotHierarchyOptions,
): { roots: HierarchyTreeNode[]; orphanCount: number; cyclicNames: string[] } {
  const { parentKey, nameKey, valueKey, idKey, colorByKey } = opts;
  // Identity column: explicit `idKey` when supplied, otherwise the
  // name column itself (the legacy behaviour). `effectiveIdKey` is
  // only used to *read* the row; downstream we always reason in terms
  // of the resolved id string.
  const effectiveIdKey = idKey ?? nameKey;
  const byId = new Map<string, HierarchyTreeNode>();
  const parentLookup: Record<string, string> = Object.create(null);
  for (const row of rows) {
    const name = String(row[nameKey] ?? "");
    if (!name) continue;
    const id = String(row[effectiveIdKey] ?? "");
    if (!id) continue;
    const parent = String(row[parentKey] ?? "");
    // Value handling — only attempt to read when the caller supplied
    // a `valueKey`. Distinguish "no value" (null/undefined/empty
    // string) from "real zero": `Number(null)` is `0`, which would
    // silently turn missing-value internal nodes into zero-value
    // leaves, and ECharts treats explicit 0 as a real datum.
    let value: number | undefined;
    if (valueKey) {
      const rawValue = row[valueKey];
      const hasValue =
        rawValue !== null && rawValue !== undefined && rawValue !== "";
      const n = hasValue
        ? typeof rawValue === "number"
          ? rawValue
          : Number(rawValue)
        : undefined;
      value = n !== undefined && Number.isFinite(n) ? n : undefined;
    }
    const node: HierarchyTreeNode = {
      name,
      value,
      children: [],
      id,
    };
    if (colorByKey) {
      node.colorKey = row[colorByKey];
    }
    byId.set(id, node);
    parentLookup[id] = parent;
  }

  // Cycle detection: walk each node up its parent chain by id; if we
  // see the id itself before reaching a root, the chain is cyclic.
  const cyclicIds: string[] = [];
  for (const id of byId.keys()) {
    let cursor: string | undefined = parentLookup[id];
    const seen = new Set<string>([id]);
    while (cursor && cursor.length > 0) {
      if (seen.has(cursor)) {
        cyclicIds.push(id);
        break;
      }
      seen.add(cursor);
      cursor = parentLookup[cursor];
    }
  }
  // Drop edges that would close cycles — pin those nodes at root level.
  const cyclicSet = new Set(cyclicIds);

  const roots: HierarchyTreeNode[] = [];
  let orphanCount = 0;
  for (const [id, node] of byId) {
    const parent = parentLookup[id] ?? "";
    if (!parent || cyclicSet.has(id)) {
      roots.push(node);
      continue;
    }
    const parentNode = byId.get(parent);
    if (!parentNode) {
      orphanCount += 1;
      roots.push(node);
      continue;
    }
    parentNode.children!.push(node);
  }
  // Tidy: drop empty children arrays so ECharts shows leaves cleanly.
  for (const node of byId.values()) {
    if (node.children && node.children.length === 0) {
      delete node.children;
    }
  }

  // Map cycle ids back to display names so diagnostics are
  // operator-legible. When `idKey` wasn't set, name == id and this is
  // a no-op; when it was, the operator sees the names they typed
  // rather than the opaque id strings.
  const cyclicNames = cyclicIds
    .map((id) => byId.get(id)?.name ?? id)
    .filter((s) => s.length > 0);
  return { roots, orphanCount, cyclicNames };
}

/**
 * Back-compat shim for the previous positional API used by the
 * sunburst component and existing tests. New call sites should reach
 * for `pivotHierarchyRows` directly.
 */
export function pivotSunburstRows(
  rows: ReadonlyArray<Record<string, unknown>>,
  parentKey: string,
  nameKey: string,
  valueKey: string,
): { roots: HierarchyTreeNode[]; orphanCount: number; cyclicNames: string[] } {
  return pivotHierarchyRows(rows, { parentKey, nameKey, valueKey });
}

/** Maximum depth of a pivoted hierarchy — caller enforces any cap. */
export function hierarchyMaxDepth(
  roots: ReadonlyArray<HierarchyTreeNode>,
): number {
  let max = 0;
  function walk(node: HierarchyTreeNode, d: number): void {
    if (d > max) max = d;
    if (node.children) for (const c of node.children) walk(c, d + 1);
  }
  for (const r of roots) walk(r, 1);
  return max;
}

/** Back-compat alias retained for the sunburst component / tests. */
export const sunburstMaxDepth = hierarchyMaxDepth;

/** ECharts sankey shape: `nodes[]` with unique names + `links[]` of edges. */
export interface SankeyShape {
  nodes: { name: string }[];
  links: { source: string; target: string; value: number }[];
  cycleEdges: number;
}

/**
 * Pivot flat `{ source, target, value }` rows into ECharts' sankey
 * shape. Cycle handling controlled by `onCycle`:
 *
 *  - `"warn"` (default): cycle edges rewritten as self-loops on the
 *    source node so ECharts' DAG layout can place them; `cycleEdges`
 *    reports the count and the caller emits `sankey_cycle_detected`
 *    so authors / telemetry see the cycle was present. Under the
 *    hood this is the same transformation as `"self-loop"` — the
 *    distinct diagnostic code lets telemetry tell "default-fired"
 *    apart from "author explicitly chose self-loop".
 *  - `"self-loop"`: identical render output to `"warn"`; emits
 *    `sankey_cycle_self_looped` instead.
 *  - `"drop"`: cycle edges filtered out entirely; emits
 *    `sankey_cycle_dropped`.
 *
 * Rationale for the warn → self-loop default: ECharts sankey is a
 * strict DAG layout. Cyclic input rendered "as-is" produces an empty
 * canvas (silent layout failure). Rewriting back-edges as self-loops
 * is the only mode that lets the viewer see *something* useful for
 * cyclic data; the diagnostic preserves the "warn" semantics for
 * the host. See `docs/agents/profile/ai-author-claude.md` review on
 * the original empty-render failure mode.
 *
 * Node-name dedupe uses `Object.create(null)` so author-controlled
 * names like `"__proto__"` can't reach Object.prototype.
 */
export function pivotSankeyRows(
  rows: ReadonlyArray<Record<string, unknown>>,
  sourceKey: string,
  targetKey: string,
  valueKey: string,
  onCycle: "warn" | "self-loop" | "drop" = "warn",
): SankeyShape {
  const nodeSet: Record<string, true> = Object.create(null);
  const links: { source: string; target: string; value: number }[] = [];
  // Adjacency for cycle detection — Map keyed by source name.
  const adj = new Map<string, Set<string>>();
  for (const row of rows) {
    const source = String(row[sourceKey] ?? "");
    const target = String(row[targetKey] ?? "");
    if (!source || !target) continue;
    const rawValue = row[valueKey];
    const value = typeof rawValue === "number" ? rawValue : Number(rawValue);
    if (!Number.isFinite(value)) continue;
    nodeSet[source] = true;
    nodeSet[target] = true;
    links.push({ source, target, value });
    let bucket = adj.get(source);
    if (!bucket) {
      bucket = new Set();
      adj.set(source, bucket);
    }
    bucket.add(target);
  }
  // Cycle detection via DFS back-edge classification. An edge is a
  // back edge iff its target is an ancestor in the current DFS path
  // (i.e. on the recursion stack). Only back edges close cycles —
  // tree, forward, and cross edges are kept intact.
  //
  // For a 2-cycle A↔B with rows [A→B, B→A] and DFS starting at A:
  //   - A→B is a tree edge (B was unvisited)
  //   - B→A is a back edge (A is on the recursion stack)
  // Result: only B→A is flagged as cycle-closing, so `self-loop` mode
  // converts only that edge to a B→B self-loop, preserving the A→B
  // forward flow.
  const cycleIndices = new Set<number>();
  const edgesBySource = new Map<string, Array<{ target: string; idx: number }>>();
  for (let i = 0; i < links.length; i++) {
    const link = links[i];
    if (!link) continue;
    let bucket = edgesBySource.get(link.source);
    if (!bucket) {
      bucket = [];
      edgesBySource.set(link.source, bucket);
    }
    bucket.push({ target: link.target, idx: i });
  }
  const visited = new Set<string>();
  const onStack = new Set<string>();
  // Iterative DFS to avoid blowing the call stack on deep graphs.
  const dfs = (root: string): void => {
    type Frame = { node: string; iter: Iterator<{ target: string; idx: number }> };
    const stack: Frame[] = [];
    visited.add(root);
    onStack.add(root);
    stack.push({
      node: root,
      iter: (edgesBySource.get(root) ?? [])[Symbol.iterator](),
    });
    while (stack.length) {
      const frame = stack[stack.length - 1]!;
      const next = frame.iter.next();
      if (next.done) {
        onStack.delete(frame.node);
        stack.pop();
        continue;
      }
      const { target, idx } = next.value;
      if (target === frame.node) {
        // Already a self-loop in the input.
        cycleIndices.add(idx);
        continue;
      }
      if (onStack.has(target)) {
        // Back edge — closes a cycle.
        cycleIndices.add(idx);
        continue;
      }
      if (!visited.has(target)) {
        visited.add(target);
        onStack.add(target);
        stack.push({
          node: target,
          iter: (edgesBySource.get(target) ?? [])[Symbol.iterator](),
        });
      }
      // Forward / cross edges: not flagged.
    }
  };
  for (const name of Object.keys(nodeSet)) {
    if (!visited.has(name)) dfs(name);
  }
  let resolvedLinks = links;
  if (onCycle === "drop") {
    resolvedLinks = links.filter((_, i) => !cycleIndices.has(i));
  } else {
    // Both `"warn"` (default) and `"self-loop"` rewrite cycle edges
    // as self-loops on the source node. The two modes differ only in
    // which diagnostic code the caller emits — the rendered output
    // is identical. ECharts can't lay out a cyclic graph, so leaving
    // back-edges intact (the old `"warn"` behaviour) produced an
    // empty canvas.
    resolvedLinks = links.map((link, i) =>
      cycleIndices.has(i)
        ? { source: link.source, target: link.source, value: link.value }
        : link,
    );
  }
  const nodes = Object.keys(nodeSet).map((name) => ({ name }));
  return { nodes, links: resolvedLinks, cycleEdges: cycleIndices.size };
}
